/* eslint react/jsx-props-no-spreading: 0 */
import React, { cloneElement, useState } from 'react';

import cn from 'classnames';
import { isEmpty, noop } from 'lodash';
import { string, bool, func, oneOf, shape } from 'prop-types';

import Popover from '@mui/material/Popover';

import BaseButton from 'components/Common/Buttons/ButtonV3/BaseButton';
import ShadowButton from 'components/Common/ShadowButtonV2';
import Tooltip from 'components/Common/Tooltip';

import v from 'components/variables';

import s from './Button.css';

const colors = {
  blue: v.blueDark,
  red: v.redDark,
  gray: v.grayDark,
  white: v.white,
};

const Button = ({
  color,
  variant,
  onClick,
  disabled,
  forceBusy,
  className,
  compactVariant,
  onActionComplete,
  confirmationText,
  confirmationOpenColor,
  confirmationButtonColor,
  confirmationButtonText,
  confirmationPopover,
  confirmationPopoverCancelButtonText,
  popoverPaperClassName,
  hasWhiteBackground,
  hasNoWrap,
  innerLabel,
  hasFocusUnderline,
  arrow,
  arrowDirection,
  popoverProps,
  tooltipProps,
  isElementary,
  ...restProps
}) => {
  const [busy, setBusy] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const openPopover = ({ currentTarget }) => setAnchorEl(currentTarget);

  const closePopover = event => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const isPopoverOpen = Boolean(anchorEl);
  const popoverId = isPopoverOpen ? 'popover' : undefined;
  const ButtonComponent = isElementary ? ShadowButton : BaseButton;

  let selectedColor = disabled ? v.grayMedium : colors[color] || v.blueDark;

  if (isPopoverOpen && confirmationOpenColor) {
    selectedColor = colors[confirmationOpenColor];
  }

  const onButtonClick = async e => {
    setBusy(true);
    await onClick(e);
    setBusy(false);
    onActionComplete();
  };

  const renderButton = () => {
    const buttonComponent = (
      <ButtonComponent
        type="button"
        variant={variant}
        disabled={disabled}
        selectedColor={selectedColor}
        compactVariant={compactVariant}
        hasWhiteBackground={hasWhiteBackground}
        hasNoWrap={hasNoWrap}
        hasFocusUnderline={hasFocusUnderline}
        className={cn(className, {
          [s.buttonLoadingAnimation]: busy || forceBusy,
        })}
        onClick={async e => {
          if (busy || disabled) return;
          if (confirmationText || confirmationPopover) {
            e.stopPropagation();
            openPopover(e);
          } else {
            await onButtonClick(e);
          }
        }}
        innerLabel={innerLabel}
        color={color}
        {...restProps}
      />
    );

    if (!isEmpty(tooltipProps) && disabled) {
      return cloneElement(
        <Tooltip>
          <span>{buttonComponent}</span>
        </Tooltip>,
        tooltipProps,
      );
    }
    return buttonComponent;
  };

  return (
    <>
      {renderButton()}
      <Popover
        id={popoverId}
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        classes={{
          root: s.popoverRoot,
          paper: cn(s.popoverPaper, popoverPaperClassName, {
            [s.arrow]: arrow,
            [s.arrowTop]: arrowDirection === 'top',
            [s.arrowBottom]: arrowDirection === 'bottom',
          }),
        }}
        {...popoverProps}
      >
        {confirmationPopover ? (
          confirmationPopover({ closePopover })
        ) : (
          <div>
            <div className={s.popoverText}>{confirmationText}</div>
            <div className={s.popoverButtonContainer}>
              <Button
                label={confirmationPopoverCancelButtonText}
                color="gray"
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  closePopover();
                }}
              />
              <Button
                label={confirmationButtonText}
                color={confirmationButtonColor}
                variant="outlined"
                size="small"
                onClick={async e => {
                  e.stopPropagation();
                  await onButtonClick(e);
                  closePopover();
                }}
              />
            </div>
          </div>
        )}
      </Popover>
    </>
  );
};

Button.propTypes = {
  onClick: func,
  disabled: bool,
  forceBusy: bool,
  className: string,
  onActionComplete: func,
  confirmationText: string,
  confirmationOpenColor: string,
  confirmationButtonText: string,
  confirmationButtonColor: string,
  confirmationPopoverCancelButtonText: string,
  color: oneOf(['blue', 'red', 'gray']),
  variant: oneOf(['filled', 'outlined', 'text']),
  compactVariant: oneOf(['default', 'select']),
  confirmationPopover: func,
  popoverPaperClassName: string,
  hasWhiteBackground: bool,
  hasNoWrap: bool,
  // Specify an innerLabel value if you wish to display text within the button rather than below when compactVariant is 'select' or 'default'.
  innerLabel: string,
  hasFocusUnderline: bool,
  arrow: bool,
  arrowDirection: oneOf(['top', 'bottom']),
  popoverProps: shape({}),
  tooltipProps: shape({
    /* exact props that we pass for Common/Tooltip */
  }),
  isElementary: bool,
};

Button.defaultProps = {
  color: undefined,
  variant: null,
  className: '',
  onClick: noop,
  disabled: false,
  forceBusy: false,
  compactVariant: null,
  confirmationText: '',
  onActionComplete: noop,
  confirmationOpenColor: null,
  confirmationButtonText: 'Delete',
  confirmationButtonColor: 'red',
  confirmationPopoverCancelButtonText: 'Cancel',
  confirmationPopover: null,
  popoverPaperClassName: null,
  hasWhiteBackground: false,
  hasNoWrap: false,
  innerLabel: null,
  hasFocusUnderline: true,
  arrow: false,
  arrowDirection: 'bottom',
  popoverProps: {},
  tooltipProps: {},
  isElementary: false,
};

export default Button;
